export interface ContactRow {
  id: number;
  businessName: string | null;
  contactName: string | null;
  title: string | null;
  email: string | null;
  phone: string | null;
  category: string | null;
  tier: string | null;
  score: number | null;
  dealValue: number | null;
  stageId: string | null;
  stageName: string | null;
  ownerUserId: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  companyId: number | null;
  companyName: string | null;
  lastActivityAt: string | null;
  createdAt?: string | null;
  researched?: boolean;
  grade?: string | null;
  workStatus?: string;
}

export interface Stage {
  id: string;
  name: string;
  position: number;
  isWon: number;
  isLost: number;
  color: string | null;
}

export interface Activity {
  id: number;
  type: string;
  body: string | null;
  metadata: any;
  createdAt: string;
  userName: string | null;
  userEmail: string | null;
}

export interface TaskRow {
  id: number;
  title: string;
  notes: string | null;
  dueAt: string | null;
  status: string;
  reason: string | null;
  autoGenerated: number;
  contactId: number | null;
  assignedUserId: string | null;
  assigneeName: string | null;
  contactBusiness: string | null;
  contactPerson: string | null;
}

export interface Warning {
  kind: 'collision' | 'warm_intro';
  message: string;
}

// Deep Research grade (A–F) and batch tier (Hot/Warm/Cold) are two vocabularies for
// the same question. The grade is the deeper, human-qualified signal, so where they
// disagree the grade wins and we say so (Phase 8 Tier 2: they were silently
// contradictory). Returns the tier a given grade IMPLIES, so the UI can flag a clash.
export const gradeColor = (g?: string | null) =>
  g === 'A' || g === 'B' ? 'text-success'
  : g === 'C' ? 'text-victory'
  : g ? 'text-danger'
  : 'text-muted';

export const gradeImpliedTier = (g?: string | null): string | null =>
  !g ? null : g === 'A' || g === 'B' ? 'Hot' : g === 'C' ? 'Warm' : 'Cold';

// True when the cheap tier and the researched grade point in opposite directions
// (e.g. scored Cold but researched a strong A) — worth surfacing, not hiding.
export const tierGradeConflict = (tier: string | null, grade?: string | null): boolean => {
  const implied = gradeImpliedTier(grade);
  return !!(implied && tier && implied !== tier);
};

export const tierColor = (tier: string | null) =>
  tier === 'Hot'
    ? 'text-danger'
    : tier === 'Warm'
    ? 'text-victory'
    : tier === 'Cold'
    ? 'text-electric'
    : 'text-muted';

export function relTime(iso: string | null): string {
  if (!iso) return '—';
  const then = Date.parse(iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(then)) return iso;
  const secs = Math.floor((Date.now() - then) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}
